import * as React from 'react';
// @ts-ignore
import { Sort as SortType } from 'clients/api/organizationClasses';
import { SortDirection } from 'components/ComposableTable';
import ListSidebar, {
  GROUP_PROFILE,
  SidebarData,
  UNCLAIMED_GROUP_BATCH,
  UNCLAIMED_GROUP_PROFILE,
} from 'components/ListSidebar/ListSidebar';
import Row from 'components/Row';
import View from 'components/View';
import Views from 'components/Views';
import {
  GroupRow,
  GroupStatus,
  GroupOwnerRole,
  isActiveGroupRow,
  isUnclaimedGroupRow,
} from 'groups/components/GroupList/GroupListTable';
// @ts-ignore
import GroupProfileContainer from 'groups/components/GroupProfile';
import GroupListFilterContainer from 'groups/containers/GroupList/GroupListFilterContainer';
import GroupListTableContainer from 'groups/containers/GroupList/GroupListTableContainer';
import EventTracking from 'lib/EventTracking';
// @ts-ignore
import { UsersProfileInOrgContainer } from 'users/components/UsersProfile';

import './styles/GroupList.scss';

export type Props = {
  orgUuid: string;
};

export type GroupListFilterState = {
  groupStatus: GroupStatus;
  selectedRoles: GroupOwnerRole[];
};

type State = {
  filters: GroupListFilterState;
  query: string;
  selectedGroup: GroupRow | null;
  reloadQuery: boolean;
  sidebarHistory: SidebarData[];
  currentSidebarData: SidebarData | null;
  sortType: SortType;
  sortDirection: SortDirection;
};

export default class GroupList extends React.Component<Props, State> {
  state: State = {
    filters: {
      groupStatus: 'active',
      selectedRoles: ['teacher', 'admin', 'parent', 'staff', 'student'],
    },
    query: '',
    selectedGroup: null,
    reloadQuery: false,
    currentSidebarData: null,
    sidebarHistory: [],
    sortType: 'id',
    sortDirection: 'asc',
  };

  handleQueryChange = (query: string) => {
    this.setState({ query });
  };

  handleSidebarClose = () => {
    this.setState({
      selectedGroup: null,
      currentSidebarData: null,
      sidebarHistory: [],
    });
  };

  handleSidebarEntityClicked = (newSidebarData: SidebarData) => {
    const { sidebarHistory } = this.state;
    const newHistory = sidebarHistory.slice();

    const currentSidebarData = this.getCurrentSidebarData();
    if (currentSidebarData) {
      newHistory.push(currentSidebarData);
    }

    this.setState({ sidebarHistory: newHistory, currentSidebarData: newSidebarData });
  };

  handleSidebarBackClicked = () => {
    const newHistory = this.state.sidebarHistory.slice();
    const sidebarData = newHistory.pop() || null;
    this.setState({ sidebarHistory: newHistory, currentSidebarData: sidebarData });
  };

  getCurrentSidebarData = (): SidebarData | null => {
    const { orgUuid } = this.props;
    const {
      selectedGroup,
      currentSidebarData,
      filters: { groupStatus },
    } = this.state;

    if (currentSidebarData) {
      return currentSidebarData;
    } else if (selectedGroup) {
      if (isActiveGroupRow(selectedGroup)) {
        return {
          type: GROUP_PROFILE,
          data: {
            group: selectedGroup,
            entityUuid: orgUuid,
          },
        };
      }

      if (isUnclaimedGroupRow(selectedGroup)) {
        return {
          type: UNCLAIMED_GROUP_PROFILE,
          data: {
            group: selectedGroup,
            entityUuid: orgUuid,
          },
        };
      }
    } else if (groupStatus === 'unclaimed') {
      return {
        type: UNCLAIMED_GROUP_BATCH,
        data: {
          orgUuid,
        },
      };
    }

    return null;
  };

  reloadList = () => {
    this.setState(
      {
        reloadQuery: true,
      },
      () => {
        this.setState({
          reloadQuery: false,
        });
      }
    );
  };

  trackEvent = (key: string) => {
    const { orgUuid } = this.props;

    EventTracking.trackKey(key, {
      organization_uuid: orgUuid,
    });
  };

  handleFiltersChange = (activeFilters: GroupListFilterState) => {
    const { filters: currentFilters } = this.state;

    if (activeFilters.groupStatus !== currentFilters.groupStatus) {
      this.trackEvent(`admin_dashboard.class_list.filters.${activeFilters.groupStatus}.click`);
    }
    this.setState({ filters: activeFilters });
  };

  componentDidMount() {
    this.trackEvent('admin_dashboard.class_list.filters.status.view');
  }

  renderFilters(orgUuid: string, query: string | null) {
    const { filters } = this.state;

    return (
      <GroupListFilterContainer
        filters={filters}
        onFiltersChange={this.handleFiltersChange}
        onQueryChange={this.handleQueryChange}
        onQueryClick={() => {
          this.trackEvent('admin_dashboard.class_list.search.click');
        }}
        orgUuid={orgUuid}
        query={query}
      />
    );
  }

  render() {
    const handleRemoveGroupFromOrg = () => {
      this.trackEvent('admin_dashboard.class_list.profile_view.remove_from_class.click');
    };

    const { orgUuid } = this.props;

    const {
      filters,
      query,
      selectedGroup,
      reloadQuery,
      sidebarHistory,
      sortType,
      sortDirection,
    } = this.state;

    const { groupStatus, selectedRoles } = filters;
    const currentSidebarData = this.getCurrentSidebarData();

    return (
      <Views className="GroupList">
        <View gutter="small" scroll className="GroupList-body">
          <Row size="small">{this.renderFilters(orgUuid, query)}</Row>
          <GroupListTableContainer
            orgUuid={orgUuid}
            onGroupClicked={(row: GroupRow) => {
              this.setState({
                selectedGroup: row,
                currentSidebarData: null,
                sidebarHistory: [],
              });

              this.trackEvent('admin_dashboard.class_list.table.class_name.click');
            }}
            openGroupKey={selectedGroup && selectedGroup.uuid}
            reloadQuery={reloadQuery}
            groupStatus={groupStatus as GroupStatus}
            selectedRoles={selectedRoles}
            query={query}
            onSortChange={(type: SortType, direction: SortDirection) =>
              this.setState({ sortType: type, sortDirection: direction })
            }
            sortType={sortType}
            sortDirection={sortDirection}
          />
        </View>
        {currentSidebarData && (
          <View className="GroupList-sidebar" full scroll>
            <ListSidebar
              sidebarData={currentSidebarData}
              onClose={this.handleSidebarClose}
              reloadQuery={reloadQuery}
              onUserChanged={this.reloadList}
              onEntityClicked={this.handleSidebarEntityClicked}
              onBackClicked={sidebarHistory.length > 0 ? this.handleSidebarBackClicked : undefined}
              onRemoveGroupFromOrg={handleRemoveGroupFromOrg}
              onViewAllAnnouncementsClicked={() => {
                this.trackEvent('admin_dashboard.class_list.profile_view.more_announcements.click');
              }}
              openModalRemoveGroupFromOrgEventKey={
                'admin_dashboard.class_list.profile_view.remove_from_school.click'
              }
              userProfileView={UsersProfileInOrgContainer}
              groupProfileView={GroupProfileContainer}
              isAdmin
            />
          </View>
        )}
      </Views>
    );
  }
}
